<template>
    <!--
      EuroTitansStockSection
      Teal-gradient section showing stock categories as horizontal scrollable rows.
      Each row has a category title + horizontal cards with logo placeholder + stock name.
    -->
    <section class="py-16 lg:py-[150px] overflow-hidden" :style="{ background: backgroundColor }">
        <div class="px-5 lg:px-[120px]">
            <!-- Header: Title + Icon -->
            <div class="flex flex-row items-start justify-between mb-4">
                <h2 class="text-[28px] leading-[36px] lg:text-[44px] lg:leading-[57px] font-bold" :class="titleColor">
                    <span v-html="renderTitle()"></span>
                </h2>
                <!-- Optional logo image -->
                <img
                    v-if="iconImage"
                    :src="$asset(iconImage, 'public')"
                    class="w-[70px] h-[70px] lg:w-[114px] lg:h-[114px] object-contain hidden lg:block"
                    alt="Euro-Titans"
                />
            </div>

            <!-- Subtitle -->
            <p v-if="subtitle" class="text-[18px] font-pro leading-relaxed mb-[50px]" :class="subtitleColor">
                {{ subtitle }}
            </p>

            <!-- Categories -->
            <div class="flex flex-col gap-[50px]">
                <div v-for="(category, catIndex) in categories" :key="catIndex">
                    <!-- Category Title -->
                    <h3 class="text-[24px] font-bold mb-[30px]" :style="{ color: categoryTitleColor }">
                        {{ category.title }}
                    </h3>

                    <!-- Horizontal scrollable row of stock cards -->
                    <div class="flex flex-row gap-5 overflow-x-auto pb-2 scrollbar-hide">
                        <div
                            v-for="(stock, stockIndex) in category.stocks"
                            :key="stockIndex"
                            class="flex flex-col items-center justify-center gap-[10px] rounded-xl flex-shrink-0"
                            :style="{
                                backgroundColor: cardBg,
                                width: '224px',
                                minHeight: '156px',
                                padding: '20px'
                            }"
                        >
                            <!-- Logo slot -->
                            <div class="w-[80px] h-[80px] rounded-lg overflow-hidden flex items-center justify-center" :style="{ backgroundColor: logoSlotBg }">
                                <img
                                    v-if="stock.image"
                                    :src="$asset(stock.image, 'public')"
                                    :alt="stock.name"
                                    class="w-full h-full object-contain"
                                />
                                <!-- Initials fallback when no logo -->
                                <span
                                    v-else
                                    class="text-[22px] font-bold select-none"
                                    :style="{ color: logoInitialColor }"
                                >
                                    {{ stock.name.charAt(0) }}
                                </span>
                            </div>

                            <!-- Stock name -->
                            <p class="text-center font-bold text-[20px] leading-[26px] w-full" :style="{ color: stockNameColor }">
                                {{ stock.name }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const props = defineProps({
    backgroundColor: {
        type: String,
        default: 'linear-gradient(to bottom right, #11D2D4, #0C7475)'
    },
    title: {
        type: String,
        default: ''
    },
    titleColor: {
        type: String,
        default: 'text-white'
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightColor: {
        type: String,
        default: 'text-black'
    },
    subtitle: {
        type: String,
        default: ''
    },
    subtitleColor: {
        type: String,
        default: 'text-white'
    },
    iconImage: {
        type: String,
        default: ''
    },
    categories: {
        type: Array,
        default: () => []
    },
    cardBg: {
        type: String,
        default: '#ffffff'
    },
    categoryTitleColor: {
        type: String,
        default: '#000000'
    },
    stockNameColor: {
        type: String,
        default: '#000000'
    },
    logoSlotBg: {
        type: String,
        default: '#f0fafa'
    },
    logoInitialColor: {
        type: String,
        default: '#0C7475'
    }
});

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const parts = props.title.split(props.titleHighlight);

    if (parts.length === 1) return props.title;

    return parts
        .map((part, index) => {
            if (index === 0) return part;

            return `<span class="${props.titleHighlightColor}">${props.titleHighlight}</span>${part}`;
        })
        .join('');
};
</script>

<style scoped>
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
</style>

